import get from 'lodash/get';
import { FieldValues, Path } from 'react-hook-form';
import { string } from 'yup';

import { HistoryFieldType } from 'types/histories';
import { t } from './getTranslations';

const FIELD_TYPES_ALLOWING_MULTIPLE_ACTIVE_RECORDS: ReadonlySet<string> = new Set([
    HistoryFieldType.RELATIONSHIP_PARTNER,
    HistoryFieldType.RELATIONSHIP_MANAGER,
    HistoryFieldType.RELATIONSHIP_PARTNER_EMPLOYEE,
    HistoryFieldType.RELATIONSHIP_MANAGER_EMPLOYEE,
]);

const allowsMultipleActiveRecords = (fieldType: string): boolean =>
    FIELD_TYPES_ALLOWING_MULTIPLE_ACTIVE_RECORDS.has(fieldType);

const isEmpty = (value: unknown) => {
    return (
        value === undefined ||
        value === null ||
        Number.isNaN(value) ||
        (typeof value === 'object' && Object.keys(value as object).length === 0) ||
        (typeof value === 'string' && value.trim().length === 0)
    );
};

export type ValidatorResponse = {
    isValid: boolean;
    errorMsg: string;
};

const isValidPassword = async (input: unknown, errorMsg: string): Promise<ValidatorResponse> => {
    const validator = string()
        .matches(
            /^(?!\s+)(?!.*\s+$)(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[$^*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ])[A-Za-z0-9$^*.[\]{}()?"!@#%&/\\,><':;|_~`=+\- ]{8,256}$/,
            t('auth.password-requirements')
        )
        .required(t('auth.password-required'));

    try {
        await validator.validate(input);
        return { isValid: true, errorMsg };
    } catch (e) {
        // console.error('Password validator error: ', e);

        let msg = errorMsg;
        if (e instanceof Error && e.message) {
            msg = e.message;
        }
        return { isValid: false, errorMsg: msg };
    }
};

const requireCurrentOrSpecifiedFields = async <TFormData extends FieldValues>(
    input: unknown,
    formValues: TFormData,
    fields: Path<TFormData>[],
    errorMsg: string
) => {
    const areAnyOtherFieldsValid =
        fields.map((field) => !isEmpty(get(formValues, field))).find((val) => val === true) || false;
    if (areAnyOtherFieldsValid) {
        return true;
    }
    if (isEmpty(input)) {
        return errorMsg;
    }
    return true;
};

const isDateEarlierThan = (
    input: string,
    comparable: string,
    fieldName: string | null,
    errorMessage: string | undefined = undefined
): boolean | string => {
    const inputDate = new Date(input).getTime();
    const comparableDate = new Date(comparable).getTime();

    if (inputDate > comparableDate) {
        return errorMessage !== undefined
            ? errorMessage
            : t('validation.date-should-be-earlier-than-field', { fieldName: fieldName || 'to' });
    }

    return true;
};

const isDateLaterThan = (
    input: string,
    comparable: string,
    fieldName: string | null,
    errorMessage: string | undefined = undefined
): boolean | string => {
    const inputDate = new Date(input).getTime();
    const comparableDate = new Date(comparable).getTime();

    if (inputDate < comparableDate) {
        return errorMessage !== undefined
            ? errorMessage
            : t('validation.date-should-be-later-than-field', { fieldName: fieldName || 'from' });
    }

    return true;
};

const isValidDateRange = (from: string, to: string, maxDaysDifference: number): boolean | string => {
    const fromDate = new Date(from).getTime();
    const toDate = new Date(to).getTime();

    const diffInMs = toDate - fromDate;
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInDays > maxDaysDifference) {
        return t('validation.date-range-exceeds-maximum-distance', { maxDistance: String(maxDaysDifference) });
    }

    return true;
};

const hasDateOverlapWithSiblings = (
    startDate: string,
    endDate: string | null,
    currentRecordId: number,
    fieldType: string,
    allHistories: { id: number; startDate: string; endDate: string | null }[]
): boolean | string => {
    const start = new Date(startDate).getTime();
    const end = endDate ? new Date(endDate).getTime() : Infinity;
    const allowsMultiple = allowsMultipleActiveRecords(fieldType);

    const hasOverlap = allHistories
        .filter((h) => h.id !== currentRecordId)
        .some((sibling) => {
            if (sibling.endDate === null && allowsMultiple) {
                return false;
            }
            const sibStart = new Date(sibling.startDate).getTime();
            const sibEnd = sibling.endDate ? new Date(sibling.endDate).getTime() : Infinity;
            // Single-value fields use inclusive ranges, so sharing a boundary day (or a zero-width
            // start === end period) is an overlap. Multi-value fields keep half-open ranges.
            return allowsMultiple ? start < sibEnd && sibStart < end : start <= sibEnd && sibStart <= end;
        });

    if (hasOverlap) {
        return t('history.validation.date-overlap');
    }
    return true;
};

export {
    allowsMultipleActiveRecords,
    hasDateOverlapWithSiblings,
    isDateEarlierThan,
    isDateLaterThan,
    isValidDateRange,
    isValidPassword,
    requireCurrentOrSpecifiedFields,
};
